<!-- Normal view -->
@if (this.imagesUrls().length > 0 && !fullscreen()) {
  <div class="flex h-full w-full items-center justify-center">
    <div class="relative flex h-[95%] w-[95%] items-center justify-center" (click)="$event.stopPropagation()">
      <!-- Image Display Area -->
      <div #imageContainer class="flex h-full w-full items-center justify-center">
        <img
          #imageElement
          [src]="currentImage()"
          alt="Gallery image"
          class="max-h-full max-w-full object-contain transition-transform duration-200 select-none"
          [style.transform]="getImageTransform()"
          [style.cursor]="zoomLevel() > 1 ? (isPanning() ? 'grabbing' : 'grab') : 'zoom-in'"
          [style.webkitUserDrag]="'none'"
          (mousedown)="startPan($event)"
          (mouseup)="stopPan()"
          (mouseleave)="stopPan()"
          (touchstart)="startPan($event)"
          (touchmove)="onPan($event)"
          (touchend)="stopPan()"
          (wheel)="onWheel($event)"
          (dblclick)="zoomLevel() === 1 ? zoomIn() : resetZoom()"
          (load)="onImageLoad()"
        />
      </div>

      <!-- Navigation Arrows -->
      @if (hasMultipleImages()) {
        <button
          mat-mini-fab
          class="md:xl absolute! top-1/2 left-5 z-10 -translate-y-1/2 sm:right-2.5"
          [disabled]="!hasPreviousImage()"
          (click)="previousImage()"
          aria-label="Previous image"
        >
          <mat-icon>chevron_left</mat-icon>
        </button>

        <button
          mat-mini-fab
          class="md:xl absolute! top-1/2 right-5 z-10 -translate-y-1/2 sm:right-2.5"
          [disabled]="!hasNextImage()"
          (click)="nextImage()"
          aria-label="Next image"
        >
          <mat-icon>chevron_right</mat-icon>
        </button>
      }

      <!-- Zoom Controls -->
      <div
        class="bg-opacity-70 absolute bottom-20 left-1/2 z-10 flex -translate-x-1/2 items-center gap-3 rounded-3xl bg-[var(--mat-sys-surface-container-highest)] px-4 py-2 sm:bottom-15 sm:gap-2 sm:px-3 sm:py-1.5 dark:bg-[var(--mat-sys-surface-container-lowest)]"
      >
        <button
          mat-icon-button
          class="h-8 w-8 transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-40 sm:h-7 sm:w-7"
          (click)="zoomOut()"
          [disabled]="zoomLevel() <= 0.5"
          aria-label="Zoom out"
        >
          <mat-icon class="text-[20px]">zoom_out</mat-icon>
        </button>

        <span class="min-w-[50px] text-center text-sm font-medium sm:min-w-[40px] sm:text-xs"
          >{{ zoomLevel() * 100 | number: '1.0-0' }}%</span
        >

        <button
          mat-icon-button
          class="h-8 w-8 transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-40 sm:h-7 sm:w-7"
          (click)="zoomIn()"
          [disabled]="zoomLevel() >= 3"
          aria-label="Zoom in"
        >
          <mat-icon class="text-[20px]">zoom_in</mat-icon>
        </button>

        <button
          mat-icon-button
          class="h-8 w-8 transition-colors duration-200 sm:h-7 sm:w-7"
          (click)="resetZoom()"
          aria-label="Reset zoom"
        >
          <mat-icon class="text-[20px]">refresh</mat-icon>
        </button>
      </div>

      <!-- Image Counter -->
      @if (hasMultipleImages()) {
        <div
          class="bg-opacity-70 absolute top-5 left-1/2 z-10 -translate-x-1/2 rounded-2xl bg-black px-4 py-2 text-sm font-medium text-white sm:top-2.5 sm:px-3 sm:py-1.5 sm:text-xs"
        >
          {{ currentImageIndex() + 1 }} of {{ previewImagesUrls().length }}
        </div>
      }

      <!-- Thumbnails Strip -->
      @if (hasMultipleImages() && previewImagesUrls().length > 0) {
        <div
          class="bg-opacity-70 absolute bottom-5 left-1/2 z-10 flex max-w-[80%] -translate-x-1/2 items-center gap-2 rounded-2xl bg-black p-3 sm:bottom-2.5 sm:max-w-[90%] sm:gap-1.5 sm:p-2"
        >
          @if (canScrollThumbnailsLeft()) {
            <button
              mat-icon-button
              class="h-8 w-8 flex-shrink-0 text-white! transition-colors duration-200 sm:h-7 sm:w-7"
              (click)="scrollThumbnailsLeft()"
              aria-label="Scroll thumbnails left"
            >
              <mat-icon class="text-[20px]">chevron_left</mat-icon>
            </button>
          }

          <div
            #thumbnailStrip
            class="no-scrollbar flex max-w-full flex-nowrap items-center gap-2 overflow-x-auto"
            (scroll)="onThumbnailScroll()"
          >
            @for (image of previewImagesUrls(); track $index) {
              <img
                [src]="image"
                alt="Thumbnail"
                class="h-[60px] w-[60px] flex-shrink-0 cursor-pointer rounded-md border-2 border-solid object-cover opacity-60 transition-all duration-200 hover:scale-105 hover:opacity-80 sm:h-[50px] sm:w-[50px]"
                [class.opacity-100!]="currentImageIndex() === $index"
                [style.border-color]="currentImageIndex() === $index ? 'var(--mat-sys-primary)' : 'transparent'"
                (click)="currentImageIndex.set($index); resetZoom()"
              />
            }
          </div>

          @if (canScrollThumbnailsRight()) {
            <button
              mat-icon-button
              class="h-8 w-8 flex-shrink-0 text-white! transition-colors duration-200 sm:h-7 sm:w-7"
              (click)="scrollThumbnailsRight()"
              aria-label="Scroll thumbnails right"
            >
              <mat-icon class="text-[20px]">chevron_right</mat-icon>
            </button>
          }
        </div>
      }
    </div>
  </div>
}

<!-- Fullscreen Overlay -->
@if (this.imagesUrls().length > 0 && fullscreen()) {
  <div class="fixed inset-0 z-[9999] flex items-center justify-center" (click)="closeGallery()">
    <!-- Backdrop con blur -->
    <div class="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>

    <!-- Popup container con sfondo di sistema -->
    <div
      class="surface relative z-10 flex h-[85vh] w-[90vw] flex-col overflow-hidden rounded-xl shadow-2xl"
      (click)="$event.stopPropagation()"
    >
      <!-- Header con bottone close -->
      <div class="flex items-center justify-end gap-2 p-2">
        <ng-container *ngTemplateOutlet="actionsTemplate() || null"></ng-container>
      </div>

      <!-- Preview content (stessa visualizzazione normale) -->
      <div class="flex min-h-0 w-full flex-1 items-center justify-center">
        <div class="relative flex h-[95%] w-[95%] items-center justify-center">
          <!-- Image Display Area -->
          <div #imageContainer class="flex h-full w-full items-center justify-center">
            <img
              #imageElement
              [src]="currentImage()"
              alt="Gallery image"
              class="max-h-full max-w-full object-contain transition-transform duration-200 select-none"
              [style.transform]="getImageTransform()"
              [style.cursor]="zoomLevel() > 1 ? (isPanning() ? 'grabbing' : 'grab') : 'zoom-in'"
              [style.webkitUserDrag]="'none'"
              (mousedown)="startPan($event)"
              (mouseup)="stopPan()"
              (mouseleave)="stopPan()"
              (touchstart)="startPan($event)"
              (touchmove)="onPan($event)"
              (touchend)="stopPan()"
              (wheel)="onWheel($event)"
              (dblclick)="zoomLevel() === 1 ? zoomIn() : resetZoom()"
              (load)="onImageLoad()"
            />
          </div>

          <!-- Navigation Arrows -->
          @if (hasMultipleImages()) {
            <button
              mat-mini-fab
              class="md:xl absolute! top-1/2 left-5 z-10 -translate-y-1/2 sm:right-2.5"
              [disabled]="!hasPreviousImage()"
              (click)="previousImage()"
              aria-label="Previous image"
            >
              <mat-icon>chevron_left</mat-icon>
            </button>

            <button
              mat-mini-fab
              class="md:xl absolute! top-1/2 right-5 z-10 -translate-y-1/2 sm:right-2.5"
              [disabled]="!hasNextImage()"
              (click)="nextImage()"
              aria-label="Next image"
            >
              <mat-icon>chevron_right</mat-icon>
            </button>
          }

          <!-- Zoom Controls -->
          <div
            class="bg-opacity-70 absolute bottom-20 left-1/2 z-10 flex -translate-x-1/2 items-center gap-3 rounded-3xl bg-[var(--mat-sys-surface-container-highest)] px-4 py-2 sm:bottom-15 sm:gap-2 sm:px-3 sm:py-1.5 dark:bg-[var(--mat-sys-surface-container-lowest)]"
          >
            <button
              mat-icon-button
              class="h-8 w-8 transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-40 sm:h-7 sm:w-7"
              (click)="zoomOut()"
              [disabled]="zoomLevel() <= 0.5"
              aria-label="Zoom out"
            >
              <mat-icon class="text-[20px]">zoom_out</mat-icon>
            </button>

            <span class="min-w-[50px] text-center text-sm font-medium sm:min-w-[40px] sm:text-xs"
              >{{ zoomLevel() * 100 | number: '1.0-0' }}%</span
            >

            <button
              mat-icon-button
              class="h-8 w-8 transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-40 sm:h-7 sm:w-7"
              (click)="zoomIn()"
              [disabled]="zoomLevel() >= 3"
              aria-label="Zoom in"
            >
              <mat-icon class="text-[20px]">zoom_in</mat-icon>
            </button>

            <button
              mat-icon-button
              class="h-8 w-8 transition-colors duration-200 sm:h-7 sm:w-7"
              (click)="resetZoom()"
              aria-label="Reset zoom"
            >
              <mat-icon class="text-[20px]">refresh</mat-icon>
            </button>
          </div>

          <!-- Image Counter -->
          @if (hasMultipleImages()) {
            <div
              class="bg-opacity-70 absolute top-5 left-1/2 z-10 -translate-x-1/2 rounded-2xl bg-black px-4 py-2 text-sm font-medium text-white sm:top-2.5 sm:px-3 sm:py-1.5 sm:text-xs"
            >
              {{ currentImageIndex() + 1 }} of {{ previewImagesUrls().length }}
            </div>
          }

          <!-- Thumbnails Strip -->
          @if (hasMultipleImages() && previewImagesUrls().length > 0) {
            <div
              class="bg-opacity-70 absolute bottom-5 left-1/2 z-10 flex max-w-[80%] -translate-x-1/2 items-center gap-2 rounded-2xl bg-black p-3 sm:bottom-2.5 sm:max-w-[90%] sm:gap-1.5 sm:p-2"
            >
              @if (canScrollThumbnailsLeft()) {
                <button
                  mat-icon-button
                  class="h-8 w-8 flex-shrink-0 text-white! transition-colors duration-200 sm:h-7 sm:w-7"
                  (click)="scrollThumbnailsLeft()"
                  aria-label="Scroll thumbnails left"
                >
                  <mat-icon class="text-[20px]">chevron_left</mat-icon>
                </button>
              }

              <div
                #thumbnailStrip
                class="no-scrollbar flex max-w-full flex-nowrap items-center gap-2 overflow-x-auto"
                (scroll)="onThumbnailScroll()"
              >
                @for (image of previewImagesUrls(); track $index) {
                  <img
                    [src]="image"
                    alt="Thumbnail"
                    class="h-[60px] w-[60px] flex-shrink-0 cursor-pointer rounded-md border-2 border-solid object-cover opacity-60 transition-all duration-200 hover:scale-105 hover:opacity-80 sm:h-[50px] sm:w-[50px]"
                    [class.opacity-100!]="currentImageIndex() === $index"
                    [style.border-color]="currentImageIndex() === $index ? 'var(--mat-sys-primary)' : 'transparent'"
                    (click)="currentImageIndex.set($index); resetZoom()"
                  />
                }
              </div>

              @if (canScrollThumbnailsRight()) {
                <button
                  mat-icon-button
                  class="h-8 w-8 flex-shrink-0 text-white! transition-colors duration-200 sm:h-7 sm:w-7"
                  (click)="scrollThumbnailsRight()"
                  aria-label="Scroll thumbnails right"
                >
                  <mat-icon class="text-[20px]">chevron_right</mat-icon>
                </button>
              }
            </div>
          }
        </div>
      </div>
    </div>
  </div>
}
